import { motion, useScroll, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import TickField from './TickField';

const REVEAL = {
  hidden: { y: '110%' },
  show: (i = 0) => ({
    y: '0%',
    transition: { delay: 0.15 + i * 0.09, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Line({ children, i = 0, className = '' }) {
  return (
    <span className="mask-line block">
      <motion.span variants={REVEAL} custom={i} initial="hidden" animate="show" className={className}>
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const titleY = useTransform(scrollYProgress, [0, 1], ['0%', '-24%']);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const polaroidY = useTransform(scrollYProgress, [0, 1], ['0%', '-60%']);

  // pointer-based parallax for the hero polaroid
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const on = (e) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      setTilt({ x: (e.clientX - cx) / cx, y: (e.clientY - cy) / cy });
    };
    window.addEventListener('mousemove', on);
    return () => window.removeEventListener('mousemove', on);
  }, []);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
      style={{ background: 'linear-gradient(180deg, #FAF8FF 0%, #F3E6FF 60%, #E0AAFF 100%)' }}
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <TickField />
      </motion.div>

      {/* Floating hero polaroid — coconut break, subtle mouse parallax */}
      <motion.figure
        style={{ y: polaroidY }}
        initial={{ opacity: 0, y: 40, rotate: -12 }}
        animate={{ opacity: 1, y: 0, rotate: -8 }}
        transition={{ delay: 1.9, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="hidden md:block absolute right-8 lg:right-16 top-[28vh] w-[180px] lg:w-[220px] z-20 pointer-events-none"
        data-testid="hero-polaroid"
      >
        <div
          className="relative bg-paper p-2.5 pb-10 shadow-[0_24px_60px_-20px_rgba(36,0,70,0.45)]"
          style={{
            transform: `perspective(1000px) rotateX(${tilt.y * -4}deg) rotateY(${tilt.x * 5}deg) translateY(${tilt.y * -6}px)`,
            transition: 'transform 220ms ease-out',
          }}
        >
          <span
            aria-hidden="true"
            className="absolute -top-2 left-1/2 -translate-x-1/2 w-14 h-3.5 bg-lavender/70 rotate-[3deg] shadow-sm"
            style={{ backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.25) 0 2px, transparent 2px 6px)' }}
          />
          <div className="relative overflow-hidden aspect-[4/5] bg-void/5">
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_creative-prowess/artifacts/se758up9_IMG_1270.webp"
              alt="Eileen throwing a peace sign on a foggy mountain hike"
              className="absolute top-1/2 left-1/2 max-w-none"
              style={{
                transform: 'translate(-50%, -50%) rotate(90deg)',
                width: 'auto',
                height: '135%',
              }}
              loading="eager"
            />
            <div className="absolute inset-0 pointer-events-none mix-blend-multiply"
                 style={{ background: 'linear-gradient(180deg, rgba(157,78,221,0.05), rgba(36,0,70,0.10))' }} />
          </div>
          <figcaption className="absolute left-3 right-3 bottom-2.5 flex items-center justify-between">
            <span className="mono text-[8px] uppercase tracking-[0.25em] text-orchid">IRL · 01</span>
            <span className="serif italic text-ink text-[11px] leading-none">on the mountain</span>
          </figcaption>
        </div>
      </motion.figure>

      {/* label */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.6 }}
        className="absolute top-28 md:top-32 left-6 md:left-12 mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-royal/80 z-10"
        data-testid="hero-label"
      >
        (01) &nbsp; The operator &nbsp;/&nbsp; Est. Berlin · Montréal
      </motion.div>

      {/* Kinetic title */}
      <motion.div
        style={{ y: titleY, opacity: titleOpacity }}
        className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-12 pt-[26vh] md:pt-[22vh] pb-32"
      >
        <h1 className="serif text-ink leading-[0.88] tracking-[-0.02em]" data-testid="hero-headline">
          <span className="block text-[13vw] md:text-[10.5vw] font-medium">
            <Line i={0}>Hello, I&rsquo;m</Line>
          </span>
          <span className="block text-[15vw] md:text-[13vw] font-medium italic text-grape">
            <Line i={1}>Eileen.</Line>
          </span>
          <span className="block text-[9vw] md:text-[7.2vw] font-normal mt-2 md:mt-4">
            <Line i={2}>I find solutions through</Line>
          </span>
          <span className="block text-[9vw] md:text-[7.2vw] font-normal italic text-orchid">
            <Line i={3}>human connections.</Line>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12"
          data-testid="hero-subhead"
        >
          <p className="md:col-span-6 mono text-sm md:text-base text-ink/80 leading-relaxed">
            Ten years across Banking, Accounts Receivable, and CX / Enablement / Customer Success in B2B SaaS. I turn journey maps, survey verbatims, and forty open tabs into systems that make messy human work visible.
          </p>
          <p className="md:col-span-5 md:col-start-8 mono text-sm md:text-base text-royal/90 leading-relaxed italic serif text-lg md:text-xl">
            &ldquo;Empathy, clarity, and a well-timed joke &mdash; deployed in that order.&rdquo;
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="mt-14 md:mt-24 flex items-center gap-8"
        >
          <a
            href="#work"
            data-testid="hero-cta-work"
            className="inline-flex items-center gap-3 bg-void text-paper px-6 py-3.5 mono uppercase tracking-[0.22em] text-xs hover:bg-orchid transition-colors duration-300"
          >
            The work &nbsp;→
          </a>
          <a
            href="#field-notes"
            data-testid="hero-cta-notes"
            className="mono uppercase tracking-[0.22em] text-xs text-ink/70 hover:text-ink border-b border-ink/30 hover:border-ink pb-1 transition-colors"
          >
            Read Field Notes 001
          </a>
        </motion.div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-12 mono text-[10px] uppercase tracking-[0.3em] text-royal/70 z-10"
      >
        <span className="inline-block animate-drift">↓ &nbsp; scroll</span>
      </motion.div>
    </section>
  );
}
